// cSpell:ignore Alles keer pand pand's panden
// The immediate payment of a cost: the cost popup bills the tenants right
// away, links every split to its own tenant's payment, stores the cost after
// every payment, and stores the split a group cost was billed with.
import {
  ICostDistribution,
  Invoice,
  isTenantPaidStatus,
  PaymentSplit,
} from "./types";

// The payer of a split the payment derived itself (see PaymentSplit.payerId).
const UNKNOWN_PAYER_ID = 0;
// Below this a tenant's share is rounding noise: nobody is billed for it.
const MIN_BILLABLE_AMOUNT = 0.005;

// A payment request as an order or the request store holds it: the API names
// the payer `requestPayerAccountId`.
export interface IPayerRequestRef {
  id?: number;
  requestPayerAccountId?: number;
  payerAccountId?: number;
  payerId?: number;
}

const payerOfRequest = (request: IPayerRequestRef) =>
  request.requestPayerAccountId ?? request.payerAccountId ?? request.payerId;

// The request of the split's payer under a payment order. A cost's order holds
// one request per tenant; only when the split does not know its payer is the
// order's single request its own. Another tenant's request is never returned:
// linking it stamped one tenant's split with the other's payment status.
export const findPayerRequest = <TRequest extends IPayerRequestRef>(params: {
  requests: TRequest[];
  payerId?: number;
}): TRequest | undefined => {
  const { requests, payerId } = params;
  const isPayerKnown = payerId != null && payerId !== UNKNOWN_PAYER_ID;
  if (isPayerKnown) {
    return requests.find(request => payerOfRequest(request) === payerId);
  }
  return requests.length === 1 ? requests[0] : undefined;
};

// Whether the tenants billed together live in panden with different owners.
// "Alles in één keer" pays every tenant to one beneficiary, which would hand
// one owner the money of another owner's tenant, so it needs one owner. It
// fails closed: while the owners load, or for a pand whose owner is unknown,
// they count as different.
export const hasMixedOwners = (params: {
  propertyIds: number[];
  ownerKeyByProperty: Record<number, string>;
  isLoaded: boolean;
}): boolean => {
  const { propertyIds, ownerKeyByProperty, isLoaded } = params;
  const distinctIds = [...new Set(propertyIds.filter(id => id > 0))];
  if (distinctIds.length <= 1) return false;
  if (!isLoaded) return true;
  return (
    new Set(distinctIds.map(id => ownerKeyByProperty[id] || `unknown-${id}`))
      .size > 1
  );
};

// The split to store on a group cost that is settled right away. The weights
// are the ones the breakdown used, so the detail can redo exactly that split
// (a key that covers no unit fell back to an equal split: every unit 1, and
// then the caller passes no key). Undefined when nothing was split.
export const buildCostDistribution = (params: {
  method: ICostDistribution["method"];
  key?: { id: string; name: string };
  consumptionUnit: NonNullable<ICostDistribution["consumptionUnit"]>;
  unitWeights: { unitId: string; weight: number }[];
}): ICostDistribution | undefined => {
  const { method, key, consumptionUnit, unitWeights } = params;
  if (!unitWeights.some(unit => unit.weight > 0)) return undefined;
  return {
    method,
    ...(method === "key" && key ? { keyId: key.id, keyName: key.name } : {}),
    ...(method === "consumption" ? { consumptionUnit } : {}),
    weights: Object.fromEntries(
      unitWeights.map(unit => [unit.unitId, unit.weight]),
    ),
  };
};

// Creates, one at a time, the costs that are not stored yet, and reports
// each one as soon as it is stored (with its id when the caller returns it).
// When a later one fails, a retry creates only the ones still missing: it
// used to create the first pand's cost a second time.
export const createMissingCosts = async <TPayload>(params: {
  entries: { key: string; payload: TPayload }[];
  storedIds: Record<string, string | undefined>;
  create: (payload: TPayload) => Promise<string | undefined>;
  onStored: (stored: { key: string; id?: string }) => void;
}): Promise<void> => {
  const { entries, storedIds, create, onStored } = params;
  for (const entry of entries) {
    if (!(entry.key in storedIds)) {
      // One at a time: the stored ones must be known when a later one fails.
      // eslint-disable-next-line no-await-in-loop
      const id = await create(entry.payload);
      onStored({ key: entry.key, id });
    }
  }
};

// Whether the tenant of a split was not billed yet: the split holds no
// payment and was not paid (the cost popup was closed halfway, or the
// tenant's payment could not be created).
export const isSplitStillToBill = (split: PaymentSplit): boolean =>
  !split.paymentOrderId &&
  split.paymentRequestId == null &&
  !split.requestedAt &&
  !split.paidAt &&
  split.amount > MIN_BILLABLE_AMOUNT;

// A lease of the cost's pand, or of a unit of its group.
export interface ICostLeaseRef {
  contractId: string;
  unitId: string;
  startDate: string;
  endDate: string;
  // Who can receive its payment request (its tenants and parents).
  payerAccountIds: number[];
}

// The tenants of a partly billed cost who were not billed yet, each with the
// lease and the unit their split bills. The popup stores both on a split; an
// older split is matched to the one lease in the cost's period that its payer
// is on and no other split names. A split whose lease cannot be told (deleted
// since, or its payer on two leases) is left out: nobody is billed on a guess.
export const getLeasesOfSplitsToBill = (params: {
  splits: PaymentSplit[];
  leases: ICostLeaseRef[];
  period: { from: string; to: string };
}): { split: PaymentSplit; contractId: string; propertyId: string }[] => {
  const { splits, leases, period } = params;
  const namedContractIds = new Set(
    splits.flatMap(split => (split.contractId ? [split.contractId] : [])),
  );
  const leaseOfPayer = (payerId: number) => {
    if (payerId === UNKNOWN_PAYER_ID) return undefined;
    const matches = leases.filter(
      lease =>
        lease.endDate >= period.from &&
        lease.startDate <= period.to &&
        !namedContractIds.has(lease.contractId) &&
        lease.payerAccountIds.includes(payerId),
    );
    return matches.length === 1 ? matches[0] : undefined;
  };

  const resolved = splits.filter(isSplitStillToBill).flatMap(split => {
    const lease = split.contractId
      ? leases.find(candidate => candidate.contractId === split.contractId)
      : leaseOfPayer(split.payerId);
    if (!lease) return [];
    return [
      {
        split,
        contractId: lease.contractId,
        propertyId:
          split.propertyId != null ? String(split.propertyId) : lease.unitId,
      },
    ];
  });
  // Two splits on one lease cannot both be its tenant's share.
  const isClaimedTwice = (contractId: string) =>
    resolved.filter(other => other.contractId === contractId).length > 1;
  return resolved.filter(entry => !isClaimedTwice(entry.contractId));
};

// A tenant of a partly billed cost who was just billed for their split.
export interface IBilledSplit {
  splitId: string;
  orderId: number;
  requestId?: number;
  // Who the order bills, also when the payment form asked for the payer.
  payerId?: number;
  payerName?: string;
  contractId: string;
  propertyId?: number;
  // What the payment asks on top of the split's amount (see PaymentSplit):
  // the supplier's VAT it included, or the VAT rate of our own it adds.
  supplierVat?: number;
  outgoingVatPct?: number;
}

// The splits of a partly billed cost once these tenants were billed: each
// gets its payment order (and request), the payer that order bills, its
// lease and unit, the VAT that payment asks, and the day it was asked. Every
// other split stays exactly as stored, and a split that holds a payment
// already is never linked again.
export const withBilledSplits = (params: {
  splits: PaymentSplit[];
  billed: IBilledSplit[];
  requestedAt: string;
}): PaymentSplit[] => {
  const { splits, billed, requestedAt } = params;

  return splits.map(split => {
    const entry = billed.find(candidate => candidate.splitId === split.id);
    if (!entry || !isSplitStillToBill(split)) return split;
    return {
      ...split,
      payerId: entry.payerId ?? split.payerId,
      payerName: entry.payerName ?? split.payerName,
      contractId: entry.contractId,
      ...(entry.propertyId != null ? { propertyId: entry.propertyId } : {}),
      ...(entry.supplierVat != null ? { supplierVat: entry.supplierVat } : {}),
      // A rate of a payment withdrawn since does not carry over.
      outgoingVatPct: entry.outgoingVatPct,
      paymentOrderId: String(entry.orderId),
      ...(entry.requestId != null ? { paymentRequestId: entry.requestId } : {}),
      requestedAt,
    };
  });
};

// The splits of a cost as the payment of its tenants splits it, before any
// payment is linked: who carries how much, on which lease, with their part of
// the supplier's VAT. An edited cost is stored with these first and gets its
// payment links after, so the edit logs one change in the cost's history and
// the links alone none.
export const withoutPaymentLinks = (splits: PaymentSplit[]): PaymentSplit[] =>
  splits.map(split => ({
    ...split,
    outgoingVatPct: undefined,
    paidAt: undefined,
    paymentOrderId: undefined,
    paymentRequestId: undefined,
    requestedAt: undefined,
  }));

// The live status of the payments, per payment order and per payment request,
// as the cost lists load it (see usePaymentStatusMaps).
interface IPaymentStatusLookup {
  byOrderId: Record<number, { status: string } | undefined>;
  byRequestId: Record<number, { status: string } | undefined>;
}

// Whether one of these costs still has a payment the tenant can pay, by the
// live status of its requests: a linked split that is not paid, whose request
// is known and not paid either. The cost-level "payment requested" date and a
// link whose request is not (or no longer) in the list do not count: the
// server checks the real requests when the cost is deleted and its refusal is
// explained then (deleteRefusedMessage).
export const costsHaveOpenPayment = (params: {
  invoices: Invoice[];
  statusMaps: IPaymentStatusLookup;
}): boolean => {
  const { invoices, statusMaps } = params;

  return invoices.some(invoice =>
    (invoice.paymentSplits ?? []).some(split => {
      if (split.paidAt) return false;
      const status =
        (split.paymentRequestId != null
          ? statusMaps.byRequestId[split.paymentRequestId]
          : undefined) ??
        (split.paymentOrderId != null
          ? statusMaps.byOrderId[Number(split.paymentOrderId)]
          : undefined);
      return status != null && !isTenantPaidStatus(status.status);
    }),
  );
};
